import { Controller } from "@hotwired/stimulus"

// Fila de "Mis productos" / "Productos archivados": switch optimista y, bajo 1200 px, deslizar o tocar
// para ver las acciones (archivar y restaurar los confirman product-archive / product-restore).
// Doc: _docs/features/products/partner_products_index.md

const ACTION_WIDTH = 144 // Editar + Archivar, 72 px cada uno
const OPEN_THRESHOLD = ACTION_WIDTH / 2
const FLICK_VELOCITY = 0.6 // px/ms
const DESKTOP_QUERY = "(min-width: 1200px)"

// A lo sumo una fila abierta a la vez
let openRow = null

export default class extends Controller {
  static targets = ["cell", "switch", "stateLabel", "actions"]
  static values = { url: String, archiveUrl: String, restoreUrl: String, name: String }

  connect() {
    // La fila puede reconectarse al reubicarse: parte cerrada
    this.position = 0
    this.dragging = false
    this.suppressClick = false
    this.pointerId = null
    this.element.classList.remove("is-open", "is-dragging")
    if (this.hasCellTarget) this.cellTarget.style.transform = ""
    this.desktop = window.matchMedia(DESKTOP_QUERY)
    this.onMediaChange = () => { if (this.desktop.matches) this.close() }
    this.desktop.addEventListener("change", this.onMediaChange)
  }

  disconnect() {
    this.desktop.removeEventListener("change", this.onMediaChange)
    if (openRow === this) openRow = null
  }

  // Switch activo/inactivo: pinta primero, confirma con el servidor y revierte si falla.
  async toggle() {
    const input = this.switchTarget
    const active = input.checked
    this.#paint(active)
    try {
      const response = await fetch(this.urlValue, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken()
        },
        body: JSON.stringify({ active })
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      input.checked = Boolean(data.active)
      this.#paint(input.checked)
    } catch (error) {
      console.error("No se pudo cambiar el estado del producto:", error)
      input.checked = !active
      this.#paint(!active)
      window.toastr?.error("No se pudo cambiar el estado del producto. Inténtalo de nuevo.")
    }
  }

  // El área del switch no abre ni cierra el panel de acciones
  stop(event) {
    event.stopPropagation()
  }

  askArchive(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dispatch("archive", {
      detail: { name: this.nameValue, url: this.archiveUrlValue, row: this.element },
      bubbles: true
    })
  }

  // Restaurar: mismo contrato que archivar (product-restore)
  askRestore(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dispatch("restore", {
      detail: { name: this.nameValue, url: this.restoreUrlValue, row: this.element },
      bubbles: true
    })
  }

  // Teclado (bajo 1200 px): enfocar Editar o Archivar abre el panel; salir lo cierra
  revealActions() {
    if (this.desktop.matches) return
    this.open()
  }

  // El foco que pasa al diálogo de archivar no cierra el panel: al volver, el botón sigue a la vista.
  hideActions(event) {
    if (this.desktop.matches) return
    const next = event.relatedTarget
    if (next && (this.actionsTarget.contains(next) || next.closest("dialog"))) return
    this.close()
  }

  // --- Gesto: solo bajo 1200 px (en desktop las acciones están a la vista) ---

  pointerDown(event) {
    if (this.desktop.matches) return
    if (event.button !== undefined && event.button !== 0) return
    this.pointerId = event.pointerId
    this.startX = event.clientX
    this.startY = event.clientY
    this.startPosition = this.position
    this.dragging = false
    this.decided = false
    this.suppressClick = false
    this.lastX = event.clientX
    this.lastTime = event.timeStamp
    this.velocity = 0
  }

  pointerMove(event) {
    if (this.pointerId !== event.pointerId) return
    const dx = event.clientX - this.startX
    const dy = event.clientY - this.startY

    if (!this.decided) {
      // Scroll vertical dominante: soltar el gesto
      if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
        this.pointerId = null
        return
      }
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
        // A la derecha desde cerrada no hay gesto (queda el "volver" nativo de iOS)
        if (dx > 0 && this.startPosition === 0) {
          this.pointerId = null
          return
        }
        this.decided = true
        this.dragging = true
        this.element.classList.add("is-dragging")
        try { this.cellTarget.setPointerCapture(event.pointerId) } catch { /* pointer ya liberado */ }
        if (openRow && openRow !== this) openRow.close()
      } else {
        return
      }
    }

    const dt = event.timeStamp - this.lastTime
    if (dt > 0) this.velocity = (event.clientX - this.lastX) / dt
    this.lastX = event.clientX
    this.lastTime = event.timeStamp

    this.#translate(Math.min(Math.max(this.startPosition + dx, -ACTION_WIDTH), 0))
  }

  pointerUp(event) {
    if (this.pointerId !== event.pointerId) return
    this.pointerId = null
    if (!this.dragging) return
    this.dragging = false
    this.element.classList.remove("is-dragging")
    this.suppressClick = true

    const flick = this.velocity < -FLICK_VELOCITY && this.position < -12
    if (this.position < -OPEN_THRESHOLD || flick) {
      this.open()
    } else {
      this.close()
    }
  }

  pointerCancel(event) {
    if (this.pointerId !== event.pointerId) return
    this.pointerId = null
    this.dragging = false
    this.element.classList.remove("is-dragging")
    this.position < -OPEN_THRESHOLD ? this.open() : this.close()
  }

  cancelNativeDrag(event) {
    event.preventDefault()
  }

  // Tap en la fila (fuera del switch) alterna el panel; el click tras un arrastre no cuenta y con teclado el enlace navega
  tap(event) {
    if (this.suppressClick || this.dragging) {
      event.preventDefault()
      event.stopPropagation()
      this.suppressClick = false
      return
    }
    if (this.desktop.matches || event.detail === 0) return
    event.preventDefault()
    if (openRow === this) {
      this.close()
      return
    }
    if (openRow) {
      openRow.close()
      return
    }
    this.open()
  }

  open() {
    if (openRow && openRow !== this) openRow.close()
    openRow = this
    this.element.classList.add("is-open")
    this.#settle(-ACTION_WIDTH)
  }

  close() {
    if (openRow === this) openRow = null
    this.element.classList.remove("is-open")
    this.#settle(0)
  }

  #paint(active) {
    this.element.dataset.active = String(active)
    this.element.classList.toggle("is-inactive", !active)
    if (this.hasStateLabelTarget) this.stateLabelTarget.textContent = active ? "Activo" : "Inactivo"
  }

  #translate(x) {
    this.position = x
    this.cellTarget.style.transform = `translate3d(${x}px, 0, 0)`
  }

  #settle(x) {
    this.position = x
    if (!this.hasCellTarget) return
    this.cellTarget.style.transform = x === 0 ? "" : `translate3d(${x}px, 0, 0)`
  }
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}
